import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import api from '../../services/api';

const SoldProducts = () => {
  const [soldData, setSoldData] = useState([]);
  const [totalSales, setTotalSales] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSoldProducts = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/dashboard/sold-products');
        const result = response.data;
        console.log('Sold Products API Response:', result);
        if (result && result.success) {
          // Handle the actual API response format: { totalSales, platformSales }
          // NOTE: Backend SQL SUM() returns strings - must convert to numbers for Recharts
          const total = parseInt(result.data?.totalSales || result.totalSales || 0, 10);
          const platforms = result.data?.platformSales || result.platformSales || [];
          setTotalSales(total);
          // Transform platform sales to chart format with colors
          // FIX: Explicitly convert value to number - Recharts requires numeric data for pie chart
          const platformsWithColors = platforms.map((platform, index) => {
            const colors = ['#e53238', '#5e8e3e', '#731a3d', '#097770', '#ff6b6b', '#007bff', '#ffc107'];
            return {
              name: platform.name || platform.platform || `Platform ${index + 1}`,
              value: parseInt(platform.value || platform.sales_count || 0, 10),
              color: colors[index % colors.length]
            };
          });
          setSoldData(platformsWithColors);
        } else {
          setTotalSales(0);
          setSoldData([]);
        }
      } catch (error) {
        console.error('Failed to fetch sold products:', error);
        setTotalSales(0);
        setSoldData([]);
      } finally {
        setLoading(false);
      }
    };
    fetchSoldProducts();
  }, []);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="chart-tooltip">
          <p className="tooltip-label">{data.name}</p>
          <p className="tooltip-value">{data.value} sales ({totalSales > 0 ? Math.round((data.value / totalSales) * 100) : 0}%)</p>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload, data }) => {
    // Use payload from Recharts Legend or data prop from manual call
    const items = payload || data || [];

    return (
      <div className="chart-legend">
        {items.map((entry, index) => (
          <div key={index} className="legend-item">
            <div
              className="legend-color"
              style={{ backgroundColor: entry.color }}
            ></div>
            <span className="legend-name">{entry.value || entry.name}</span>
            <span className="legend-value">
              {soldData.find(item => item.name === (entry.value || entry.name))?.value || entry.value || 0}
            </span>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="dashboard-card sold-products-card">
        <div className="card-body">
          <div className="card-header">
            <h3 className="card-title">Products Sold</h3>
            <p className="card-subtitle">Sales distribution by platform</p>
          </div>
          <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
            Loading sold products data...
          </div>
        </div>
      </div>
    );
  }

  if (soldData.length === 0) {
    return (
      <div className="dashboard-card sold-products-card">
        <div className="card-body">
          <div className="card-header">
            <h3 className="card-title">Products Sold</h3>
            <p className="card-subtitle">Sales distribution by platform</p>
          </div>
          <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
            No sold products data available
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-card sold-products-card">
      <div className="card-body">
        <div className="card-header">
          <h3 className="card-title">Products Sold</h3>
          <p className="card-subtitle">Sales distribution by platform</p>
        </div>

        <div className="chart-container">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={soldData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="value"
              >
                {soldData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>

          <CustomLegend data={soldData} />
        </div>

        <div className="total-sales">
          <span className="total-label">Total Sales:</span>
          <span className="total-value">{totalSales.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
};

export default SoldProducts;
